/**
 * Turns the HTML a sender wrote into the document the body `iframe` shows.
 *
 * The goal is the email as the mailbox shows it (its own styles, tables, colours), with
 * two things a mail client also does: remote images stay off until someone asks for
 * them, and links open in a new tab. Pure and synchronous so it can be tested without a
 * browser: `DOMParser` builds an inert document (nothing loads, nothing runs) that is
 * rewritten and serialised back to a string for `srcDoc`.
 *
 * What each layer stops:
 *
 * - the `iframe` has no `allow-scripts`, so nothing in the email runs, whatever this
 *   module misses. `<script>` is removed anyway so it does not sit in the DOM.
 * - the CSP meta stops every subresource this module did not rewrite (a `url()` inside
 *   a `<style>`, a `<link>`): with remote content off, only `data:` loads.
 * - remote `<img>` lose their `src` while images are off, so the browser does not even
 *   try (and does not fill the console with CSP violations). Width, height and `alt`
 *   stay, so the layout keeps its shape, like Outlook's «Descargar imágenes».
 * - `cid:` images are part of the email: they point at this app's API instead, whose
 *   origin is the only one besides `data:` the CSP adds for them.
 */

function contentSecurityPolicy(remoteContent: boolean, inlineOrigin: string | null): string {
  const inline = inlineOrigin ? ` ${inlineOrigin}` : '';
  return remoteContent
    ? "default-src 'none'; style-src 'unsafe-inline' https:; " +
        `img-src data:${inline} https: http:; font-src data: https:`
    : `default-src 'none'; style-src 'unsafe-inline'; img-src data:${inline}; font-src data:`;
}

/**
 * The images the email carries inside itself, by `Content-ID` (contract v10.19): the
 * logo of a signature, a photo pasted into the message. The HTML points at them with
 * `<img src="cid:...">`; the API serves each one from this app's origin, so drawing them
 * tells the sender nothing and they load with remote images off.
 */
export interface InlineImages {
  /** `Content-ID` (lower case, no angle brackets) → the URL that serves its bytes. */
  byContentId: ReadonlyMap<string, string>;
  /** The origin those URLs live on, which the frame's CSP has to let through. */
  origin: string;
}

/** `cid:Logo%40Proveedor` → `logo@proveedor`, or null when it is not a `cid:` URL. */
export function contentIdOf(src: string | null): string | null {
  const trimmed = src?.trim() ?? '';
  if (!/^cid:/i.test(trimmed)) return null;
  let value = trimmed.slice(4);
  try {
    value = decodeURIComponent(value);
  } catch {
    // A stray `%` is not worth losing the image over: compare it as it came.
  }
  return value.replace(/^<|>$/g, '').toLowerCase() || null;
}

/** The `content_id` of an attachment, normalised like the `cid:` that points at it. */
export function attachmentContentId(attachment: { content_id?: string | null }): string | null {
  return attachment.content_id ? contentIdOf(`cid:${attachment.content_id}`) : null;
}

/** Every `Content-ID` the body points at, so those attachments are not listed twice. */
export function referencedContentIds(html: string | null | undefined): Set<string> {
  const found = new Set<string>();
  for (const match of (html ?? '').matchAll(/cid:[^"'\s)>]+/gi)) {
    const id = contentIdOf(match[0]);
    if (id) found.add(id);
  }
  return found;
}

/**
 * Where each inline image of an email is served: `GET .../attachments/{att}/image`.
 * `apiUrl` is where the API lives; relative (same host as the app) or absolute.
 */
export function inlineImagesFor(
  emailId: string,
  attachments: ReadonlyArray<{ id: string; content_id?: string | null }>,
  apiUrl: string,
  pageUrl: string,
): InlineImages {
  const byContentId = new Map<string, string>();
  for (const attachment of attachments) {
    const id = attachmentContentId(attachment);
    if (id && !byContentId.has(id)) {
      byContentId.set(id, `${apiUrl}/api/emails/${emailId}/attachments/${attachment.id}/image`);
    }
  }
  return { byContentId, origin: new URL(apiUrl || '/', pageUrl).origin };
}

/**
 * What a mail client puts around a body that brings no styles of its own. First in
 * `<head>`, so any `<style>` of the email wins over it.
 */
const BASE_STYLE =
  'body{margin:0;padding:16px 20px;background:#fff;color:#222;' +
  'font-family:Arial,Helvetica,sans-serif;font-size:14px;line-height:1.45;' +
  'word-wrap:break-word}' +
  'a{color:#1a5fb4}' +
  'blockquote{margin:0 0 0 .8ex;border-left:2px solid #ccc;padding-left:1ex}';

/** Elements that have no business in a rendered email body. */
const DROP = 'script, noscript, base, iframe, frame, frameset, object, embed, applet';

const LINK_SCHEMES = /^(https?:|mailto:|tel:)/i;
const INLINE_IMAGE = /^data:image\//i;
const REMOTE_URL_IN_CSS = /url\(\s*['"]?\s*(?!data:)[^'")\s]/i;

export interface EmailDocument {
  /** The `srcDoc`. */
  html: string;
  /**
   * The body would load something from outside (an image, a background, a stylesheet).
   * It decides whether the «Mostrar imágenes» bar is worth showing.
   */
  hasRemoteContent: boolean;
}

function isRemote(url: string | null): boolean {
  if (url === null) return false;
  const trimmed = url.trim();
  return trimmed !== '' && !INLINE_IMAGE.test(trimmed);
}

export function buildEmailDocument(
  source: string,
  {
    remoteContent,
    inlineImages = null,
  }: { remoteContent: boolean; inlineImages?: InlineImages | null },
): EmailDocument {
  const doc = new DOMParser().parseFromString(source, 'text/html');
  let hasRemoteContent = false;

  doc.querySelectorAll(DROP).forEach((node) => node.remove());
  doc.querySelectorAll('meta').forEach((meta) => {
    // A refresh would navigate the frame away; a CSP of the sender's would mix with ours.
    if (meta.hasAttribute('http-equiv')) meta.remove();
  });
  doc.querySelectorAll('link').forEach((link) => {
    if (/stylesheet/i.test(link.getAttribute('rel') ?? '')) hasRemoteContent = true;
    else link.remove();
  });

  doc.querySelectorAll('img').forEach((img) => {
    const src = img.getAttribute('src');
    const srcset = img.getAttribute('srcset');
    const contentId = contentIdOf(src);
    if (contentId !== null) {
      // An image carried inside the email itself: served by this app, never remote.
      // One the API does not know (an email from before v10.19) keeps only its `alt`.
      const served = inlineImages?.byContentId.get(contentId);
      if (served) img.setAttribute('src', served);
      else img.removeAttribute('src');
      img.removeAttribute('srcset');
      return;
    }
    if (!isRemote(src) && srcset === null) return;
    hasRemoteContent = true;
    if (!remoteContent) {
      img.removeAttribute('src');
      img.removeAttribute('srcset');
    }
  });

  doc.querySelectorAll('[background]').forEach((node) => {
    if (!isRemote(node.getAttribute('background'))) return;
    hasRemoteContent = true;
    if (!remoteContent) node.removeAttribute('background');
  });

  if (!hasRemoteContent) {
    const css = [
      ...Array.from(doc.querySelectorAll('style'), (style) => style.textContent ?? ''),
      ...Array.from(doc.querySelectorAll('[style]'), (node) => node.getAttribute('style') ?? ''),
    ];
    hasRemoteContent = css.some((text) => REMOTE_URL_IN_CSS.test(text));
  }

  doc.querySelectorAll('a').forEach((anchor) => {
    const href = anchor.getAttribute('href')?.trim() ?? '';
    if (href.startsWith('#')) return; // An anchor inside the email: it scrolls, it stays.
    if (LINK_SCHEMES.test(href)) {
      anchor.setAttribute('target', '_blank');
      anchor.setAttribute('rel', 'noopener noreferrer');
    } else {
      // `javascript:`, relative paths (they would resolve against this app) and junk.
      anchor.removeAttribute('href');
    }
    anchor.removeAttribute('ping');
  });
  doc.querySelectorAll('form').forEach((form) => {
    form.removeAttribute('action');
  });

  const head = doc.head;
  const csp = doc.createElement('meta');
  csp.setAttribute('http-equiv', 'Content-Security-Policy');
  const servesInline = inlineImages !== null && inlineImages.byContentId.size > 0;
  csp.setAttribute(
    'content',
    contentSecurityPolicy(remoteContent, servesInline ? inlineImages.origin : null),
  );
  const referrer = doc.createElement('meta');
  referrer.setAttribute('name', 'referrer');
  referrer.setAttribute('content', 'no-referrer');
  const style = doc.createElement('style');
  style.textContent = BASE_STYLE;
  head.prepend(csp, referrer, style);

  // The sender's doctype, or none: quirks mode is what their HTML was tested in.
  const doctype = doc.doctype ? new XMLSerializer().serializeToString(doc.doctype) : '';
  return { html: doctype + doc.documentElement.outerHTML, hasRemoteContent };
}

export type TextPart = { kind: 'text'; value: string } | { kind: 'link'; value: string };

const URL_IN_TEXT = /\b(?:https?:\/\/|www\.)[^\s<>"]+/gi;
const TRAILING_PUNCTUATION = /[.,;:!?)\]}'»]+$/;

/**
 * Splits a plain text body into text and links, as a mail client underlines them. The
 * punctuation that closes a sentence or a parenthesis is not part of the link, unless the
 * URL opened that parenthesis itself (`…/Foo_(bar)`).
 */
export function linkifyText(text: string): TextPart[] {
  const parts: TextPart[] = [];
  let last = 0;
  for (const match of text.matchAll(URL_IN_TEXT)) {
    let url = match[0];
    const trailing = TRAILING_PUNCTUATION.exec(url)?.[0] ?? '';
    let keep = url.length - trailing.length;
    // Give back a closing parenthesis the URL itself opened.
    for (const char of trailing) {
      const candidate = url.slice(0, keep + 1);
      const opened = (candidate.match(/\(/g) ?? []).length;
      const closed = (candidate.match(/\)/g) ?? []).length;
      if (char === ')' && opened >= closed) keep += 1;
      else break;
    }
    url = url.slice(0, keep);
    const start = match.index;
    if (start > last) parts.push({ kind: 'text', value: text.slice(last, start) });
    parts.push({ kind: 'link', value: url });
    last = start + url.length;
  }
  if (last < text.length) parts.push({ kind: 'text', value: text.slice(last) });
  return parts;
}

export function linkHref(url: string): string {
  return /^www\./i.test(url) ? `https://${url}` : url;
}
